import { useEffect, useRef, useState } from "react";
import {
  AccountInfo,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { Avatar } from "primereact/avatar";
import defaultAvatar from "../../../assets/imgs/defaultAvatar.png";
import { Divider } from "primereact/divider";
import { useMsal } from "@azure/msal-react";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import { getMe, getPhoto } from "../../../services/MsGraphApiCall";
import { FILTER_PROFILE } from "../../../diccionary/Diccionary";
import axios from "axios";
import { IUserAccess } from "../../../interfaces/ISeleccion";
import NavDropdown from "./NavDropdown";
import NavItem from "./NavItem";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const HELP_DESK_URL = "https://mesadeayuda.team-work.cl";

type MainTopbarProps = {
  children?: JSX.Element | JSX.Element[];
  onToggleMenuClick: (event: React.MouseEvent) => void;
  active: string;
  profile: string;
  itemMenu?: string;
  setItemMenu?: Function;
  setItemMenuFilter?: Function;
  // Opcional: si se provee, un clic en el logo llama a este handler en lugar de
  // navegar (se usa para el disparador oculto de "Sincronizar registros").
  onLogoClick?: () => void;
};

type GraphData = {
  displayName: string;
  givenName: string;
  jobTitle: string;
  mail: string;
  mobilePhone: string;
  officeLocation: string;
  preferredLanguage: string;
  surname: string;
  userPrincipalName: string;
  id: string;
};

const authRequest = { scopes: ["User.Read"] };

const SHARED_PATHS = ["/procesos", "/registros"];

const MASIVA_OPTIONS = [
  { label: "Procesos", to: "/procesos/activos", shared: true },
  {
    label: "Selección",
    to: "/seleccionmasiva/porvalidar",
    shared: false,
    activePaths: ["/seleccionmasiva"],
  },
];

const PROFESIONAL_OPTIONS = [
  { label: "Procesos", to: "/procesos/activos", shared: true },
  {
    label: "Selección",
    to: "/seleccionprofesional/porvalidar",
    shared: false,
    activePaths: ["/seleccionprofesional"],
  },
];

const MainTopbar: React.FC<MainTopbarProps> = ({ profile, onLogoClick }) => {
  const { instance, inProgress } = useMsal();
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [itemMasivo, setItemMasivo] = useState<boolean[] | undefined>(undefined);
  const [itemProf, setItemProf] = useState<boolean[] | undefined>(undefined);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const usuarioId = window.sessionStorage.getItem("usuarioId") as string;
  const token = window.sessionStorage.getItem("token") as string;

  const menu = useRef<any>(null);

  useEffect(() => {
    if (token && usuarioId) getUserAccess({ token });
  }, [token]);

  useEffect(() => {
    const acquireToken = () => {
      instance.acquireTokenRedirect({
        ...authRequest,
        account: instance.getActiveAccount() as AccountInfo,
      });
    };
    if (!graphData && inProgress === InteractionStatus.None) {
      getMe(instance)
        .then(setGraphData)
        .catch((e) => {
          if (e instanceof InteractionRequiredAuthError) acquireToken();
        });
      getPhoto(instance)
        .then(setPhoto)
        .catch((e) => {
          if (e instanceof InteractionRequiredAuthError) acquireToken();
        });
    }
  }, [instance, graphData, photo, inProgress]);

  const getUserAccess = async ({ token }: { token: string }) => {
    try {
      const result = await axios.get(
        `${process.env.REACT_APP_API_URL}usuario/${usuarioId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = result.data.data;
      setItemMasivo(data.map(({ masivo }: IUserAccess) => masivo));
      setItemProf(data.map(({ profesional }: IUserAccess) => profesional));
    } catch (error) {
      console.error("[MainTopbar] getUserAccess:", error);
    }
  };

  const handleLogout = () => {
    instance.logoutPopup({ mainWindowRedirectUri: "/" });
  };

  const userMenuItems = [
    { label: "Perfil", icon: "pi pi-fw pi-user", command: () => { } },
    { label: "Configuraciones", icon: "pi pi-fw pi-cog" },
    { separator: true },
    { label: "Salir", icon: "pi pi-fw pi-power-off", command: handleLogout },
  ];

  // Determine logo home link based on profile
  const logoHref = (() => {
    if (profile === FILTER_PROFILE.tw_legal) return "/consultaexclusionrut";
    if (profile === FILTER_PROFILE.tw_cliente) return "/validacioncliente";
    if (profile === FILTER_PROFILE.admin_ops) return "/resumen";
    if (profile === FILTER_PROFILE.tw_ops || profile === FILTER_PROFILE.tw_contrato) return "/postulaciones?tab=formularios";
    if (profile === FILTER_PROFILE.tw_marketing) return "/postulaciones?tab=formularios";
    return "/registros/habilitados";
  })();

  // Which menus to show
  const profileIn = (...values: string[]) => values.includes(profile);

  // Operaciones y Contratos ven Postulaciones, Selección Masiva, Selección
  // Profesional e Inhabilitados. NO ven la sección de Contratos/Operaciones.
  const isOpsOrContrato = profileIn(FILTER_PROFILE.tw_ops, FILTER_PROFILE.tw_contrato);

  const showMasiva =
    profile &&
    (profileIn(FILTER_PROFILE.adm_tw, FILTER_PROFILE.adm_ti, FILTER_PROFILE.tw_admsel, FILTER_PROFILE.admin_sel) ||
      isOpsOrContrato ||
      (profile === FILTER_PROFILE.tw_sel && itemMasivo?.includes(true)));

  const showProfesional =
    profile &&
    (profileIn(FILTER_PROFILE.adm_tw, FILTER_PROFILE.adm_ti, FILTER_PROFILE.tw_admsel_prof, FILTER_PROFILE.admin_sel) ||
      isOpsOrContrato ||
      (profile === FILTER_PROFILE.tw_sel && itemProf?.includes(true)));

  const showRegistros = profile && !isOpsOrContrato && (showMasiva || showProfesional || profile === FILTER_PROFILE.tw_sel);
  const showInhabilitados = showRegistros || isOpsOrContrato;

  // Postulaciones (formularios de registro público): perfiles admin + reclutador.
  // El reclutador (tw_sel) puede ver la sección, pero no gestionar formularios
  // (crear / editar / desactivar) — eso se controla en TablaFormularios.
  const showPostulaciones =
    profile &&
    profileIn(
      FILTER_PROFILE.adm_tw,
      FILTER_PROFILE.adm_ti,
      FILTER_PROFILE.tw_admsel,
      FILTER_PROFILE.tw_admsel_prof,
      FILTER_PROFILE.admin_sel,
      FILTER_PROFILE.tw_sel,
      FILTER_PROFILE.tw_marketing,
      FILTER_PROFILE.tw_ops,
      FILTER_PROFILE.tw_contrato,
    );

  const showConsultaRut = false;

  return (
    <>
      <header className="prevetopbar stickyFunction2">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 24px",
            height: "52px",
          }}
        >
          {/* Logo */}
          <a
            href={logoHref}
            style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
            onClick={(e) => {
              if (onLogoClick) {
                e.preventDefault();
                onLogoClick();
              }
            }}
          >
            <img
              src="/assets/layout/images/logo-teamwork-chile.png"
              alt="TeamWork"
              style={{ width: "169px", height: "33px", border: 0 }}
            />
          </a>

          {/* Desktop nav */}
          <nav className="topbar-nav"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "28px",
              height: "52px",
              flex: 1,
              paddingLeft: "40px",
            }}
          >
            {showConsultaRut && (
              <NavItem
                label="Consulta de RUT"
                to="/consultaexclusionrut"
              />
            )}

            {showRegistros && (
              <NavItem
                label="Registros"
                to="/registros/habilitados"
                activePaths={["/registros/habilitados"]}
              />
            )}

            {showMasiva && (
              <NavDropdown
                label="Selección Masiva"
                selectionType="masivo"
                options={MASIVA_OPTIONS}
              />
            )}

            {showProfesional && (
              <NavDropdown
                label="Selección Profesional"
                selectionType="profesional"
                options={PROFESIONAL_OPTIONS}
              />
            )}

            {showRegistros && (
              <NavItem
                label="Traspasos"
                to="/registros/traspasos"
                activePaths={["/registros/traspasos"]}
              />
            )}

            {showPostulaciones && (
              <NavItem
                label="Postulaciones"
                to="/postulaciones"
              />
            )}

            {showInhabilitados && (
              <NavItem
                label="Inhabilitados"
                to="/inhabilitados"
                activePaths={["/inhabilitados"]}
              />
            )}
          </nav>

          {/* User area + hamburger */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
            <Tooltip target=".topbar-help-icon" position="bottom" />
            <a
              href={HELP_DESK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="topbar-help-icon"
              data-pr-tooltip="Mesa de Ayuda"
              aria-label="Mesa de Ayuda"
            >
              <HelpOutlineIcon style={{ fontSize: 28 }} />
            </a>
            {graphData?.displayName && (
              <span
                className="topbar-username"
                style={{
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  color: "#48566b",
                  maxWidth: "180px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {graphData.displayName}
              </span>
            )}
            <button
              className="p-link layout-topbar-button"
              onClick={(e) => menu.current.toggle(e)}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              <Avatar image={photo || defaultAvatar} size="large" shape="circle" />
            </button>
            <Menu model={userMenuItems} popup ref={menu} id="main_topbar_menu" />

            {/* Hamburger — hidden on desktop, shown on mobile via CSS */}
            <button
              className="topbar-hamburger"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menú"
            >
              {mobileMenuOpen
                ? <CloseIcon style={{ fontSize: 26, color: "#333" }} />
                : <MenuIcon style={{ fontSize: 26, color: "#333" }} />
              }
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <nav className="topbar-mobile-menu">
            {showConsultaRut && (
              <NavItem label="Consulta de RUT" to="/consultaexclusionrut" />
            )}
            {showRegistros && (
              <NavItem
                label="Registros"
                to="/registros/habilitados"
                activePaths={["/registros/habilitados"]}
              />
            )}
            {showMasiva && (
              <NavDropdown
                label="Selección Masiva"
                selectionType="masivo"
                options={MASIVA_OPTIONS}
                inlinePanel
              />
            )}
            {showProfesional && (
              <NavDropdown
                label="Selección Profesional"
                selectionType="profesional"
                options={PROFESIONAL_OPTIONS}
                inlinePanel
              />
            )}
            {showRegistros && (
              <NavItem
                label="Traspasos"
                to="/registros/traspasos"
                activePaths={["/registros/traspasos"]}
              />
            )}
            {showPostulaciones && (
              <NavItem
                label="Postulaciones"
                to="/postulaciones"
              />
            )}
            {showInhabilitados && (
              <NavItem
                label="Inhabilitados"
                to="/inhabilitados"
                activePaths={["/inhabilitados"]}
              />
            )}
          </nav>
        )}

        <Divider type="dashed" style={{ margin: 0 }} />
      </header>

    </>
  );
};

export default MainTopbar;
